/* ==========================================================================
   BOOST CUSTOMISATION - Boost Homepage Video
   Behaviour for sections/boost-homepage-video.liquid.

   On every page load:
     1. Pick ONE Video Slide at random (index 0 in the theme editor, so editing
        is predictable).
     2. Reveal only that slide (others stay hidden; their video is never built
        or downloaded).
     3. Decode the chosen slide's base64 video URL and build a muted, inline,
        autoplaying <video>. Only this one video is requested over the network,
        and the raw filename never appears in the page source.
     4. Trim the clip: start playback trim_start_seconds in, and loop back to the
        start point before the final trim_end_seconds, so each clip's intro and
        outro are not shown.

   Trim robustness (BOOST CUSTOMISATION):
     - The start point is enforced through several events (#t= URL hint,
       loadedmetadata, loadeddata, and a timeupdate guard) because a single seek
       at loadedmetadata does not always "stick" on progressively-loaded MP4s.
     - The end point is (re)computed on loadedmetadata AND durationchange, so a
       clip whose duration is briefly reported as unknown/Infinity is still
       trimmed once the real duration is known.
     - A clip too short for the requested trims falls back to playing in full.

   Honours prefers-reduced-motion (poster only, no video).
   ========================================================================== */
(function () {
  'use strict';

  function decodeUrl(encoded) {
    try {
      return window.atob(encoded);
    } catch (e) {
      return '';
    }
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function mountVideo(root, slide) {
    var encoded = slide.getAttribute('data-bhv-src');
    var figure = slide.querySelector('figure');
    if (!encoded || !figure) {
      return; // image-only slide: the poster already shows
    }
    if (prefersReducedMotion()) {
      return; // reduced motion: keep the poster, no video
    }

    var url = decodeUrl(encoded);
    if (!url) {
      return;
    }

    var trimStart = parseFloat(root.getAttribute('data-bhv-trim-start')) || 0;
    var trimEnd = parseFloat(root.getAttribute('data-bhv-trim-end')) || 0;
    if (trimStart < 0) trimStart = 0;
    if (trimEnd < 0) trimEnd = 0;

    var video = document.createElement('video');
    video.className = 'bhv-video';
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('preload', 'auto');
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    video.loop = false; // we loop manually within the trim window

    var poster = slide.querySelector('img');
    if (poster && poster.currentSrc) {
      video.poster = poster.currentSrc;
    }

    // Resolved trim bounds. startAt is the playback floor; endAt is the absolute
    // time to loop at (duration - trimEnd), or null until the duration is known.
    var startAt = trimStart;
    var endAt = null;

    function recomputeBounds() {
      var dur = video.duration;
      if (!isFinite(dur) || dur <= 0) {
        endAt = null; // duration not known yet; keep the start trim only
        return;
      }
      var s = trimStart;
      var e = trimEnd;
      if (dur <= s + e + 0.3) {
        // Clip too short for the requested trims: play the whole thing.
        s = 0;
        e = 0;
      }
      startAt = s;
      endAt = e > 0 ? dur - e : null;
    }

    function seekToStart() {
      try {
        video.currentTime = startAt;
      } catch (e) {}
    }

    function play() {
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          /* autoplay blocked: poster remains visible */
        });
      }
    }

    video.addEventListener('loadedmetadata', function () {
      recomputeBounds();
      if (startAt > 0) {
        seekToStart();
      }
      play();
    });

    video.addEventListener('durationchange', recomputeBounds);

    video.addEventListener('loadeddata', function () {
      if (startAt > 0 && video.currentTime < startAt - 0.05) {
        seekToStart();
      }
    });

    video.addEventListener('timeupdate', function () {
      // Enforce the start point (skip the intro) even if the initial seek missed.
      if (startAt > 0 && video.currentTime < startAt - 0.05) {
        seekToStart();
        return;
      }
      // Loop back before the outro.
      if (endAt !== null && video.currentTime >= endAt) {
        seekToStart();
        play();
      }
    });

    // Safety net: if playback reaches the real end (e.g. duration only became
    // known at the end), loop back to the start point rather than to 0.
    video.addEventListener('ended', function () {
      seekToStart();
      play();
    });

    // #t= start hint helps the browser begin at the trim point on first load.
    video.src = trimStart > 0 ? url + '#t=' + trimStart : url;

    var overlay = figure.querySelector('.img-overlay');
    if (overlay) {
      figure.insertBefore(video, overlay);
    } else {
      figure.appendChild(video);
    }
  }

  function initRoot(root) {
    if (!root || root.getAttribute('data-bhv-ready') === '1') {
      return;
    }
    root.setAttribute('data-bhv-ready', '1');

    var slides = Array.prototype.slice.call(
      root.querySelectorAll('[data-bhv-slide]')
    );
    if (!slides.length) {
      return;
    }

    var index;
    if (window.Shopify && window.Shopify.designMode) {
      index = 0; // predictable while editing in the theme editor
    } else {
      index = Math.floor(Math.random() * slides.length);
    }

    var chosen = slides[index];
    chosen.hidden = false;
    chosen.setAttribute('data-bhv-active', '1');
    mountVideo(root, chosen);
  }

  function initAll(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    var roots = ctx.querySelectorAll('[data-bhv-root]');
    Array.prototype.forEach.call(roots, initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor: re-initialise when the section is (re)loaded, and reveal the
  // block being edited so the merchant sees the effect of their changes.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var root =
      event.target &&
      event.target.closest &&
      event.target.closest('[data-bhv-root]');
    if (!root) {
      return;
    }
    var slides = Array.prototype.slice.call(
      root.querySelectorAll('[data-bhv-slide]')
    );
    slides.forEach(function (s) {
      s.hidden = s !== event.target;
    });
  });
})();
